import React from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { PageHeader, SectionCard, Kpi, CardSkeleton, EmptyState } from "@/admin/ui";
import { RefreshCw, AlertTriangle } from "lucide-react";

const fmtCents = (c) => c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => n == null ? "—" : Number(n).toLocaleString();
const fmtPct = (p) => p == null ? "—" : `${(p * 100).toFixed(2)}%`;

function KPI({ title, value, hint, insufficient, tone, testid }) {
  return (
    <Kpi
      label={title}
      value={insufficient ? "—" : value}
      sub={insufficient ? <span className="text-white/40 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Insufficient data</span> : hint}
      tone={tone}
      testid={testid}
    />
  );
}

export default function AdminAnalytics() {
  const { t } = useLang();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/api/admin/analytics/dashboard", { params: { days } });
      setData(r.data);
    } finally { setBusy(false); }
  }, [days]);
  React.useEffect(() => { load(); }, [load]);

  const rangeControls = (
    <div className="flex gap-1 flex-wrap" role="group" aria-label="Date range">
      {[7, 30, 90, 365].map((d) => (
        <button key={d} className={`text-xs px-2.5 py-1 rounded ${d === days ? "bg-white/15 text-white" : "text-white/60 hover:bg-white/5"}`}
          onClick={() => setDays(d)} data-testid={`an-days-${d}`}>{d}d</button>
      ))}
      <button className="lp-btn-ghost" onClick={load} disabled={busy} aria-label="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <PageHeader title={t("adminp.analytics.title")} description={t("adminp.analytics.subtitle")} primary={rangeControls} />

      {!data ? <CardSkeleton h="h-40" /> : (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI title={t("adminp.analytics.gross")} value={fmtCents(data.revenue.gross_cents)} insufficient={data.revenue.insufficient_data} testid="an-gross" />
          <KPI title={t("adminp.analytics.refunded")} value={fmtCents(data.revenue.refunded_cents)} tone={data.revenue.refunded_cents > 0 ? "warning" : undefined} testid="an-refunded" />
          <KPI title={t("adminp.analytics.disputed")} value={fmtCents(data.revenue.disputed_cents)} tone={data.revenue.disputed_cents > 0 ? "danger" : undefined} testid="an-disputed" />
          <KPI title={t("adminp.analytics.net")} value={fmtCents(data.revenue.net_cents)} tone="success" hint={`refund rate: ${fmtPct(data.revenue.refund_rate)}`} testid="an-net" />
          <KPI title={t("adminp.analytics.mrr")} value={fmtCents(data.mrr.mrr_cents)} insufficient={data.mrr.insufficient_data} hint={`${data.mrr.active_subscriptions} active subs`} testid="an-mrr" />
          <KPI title={t("adminp.analytics.arr")} value={fmtCents(data.mrr.arr_cents)} insufficient={data.mrr.insufficient_data} testid="an-arr" />
          <KPI title={t("adminp.analytics.arpu")} value={fmtCents(data.arpu.arpu_cents)} insufficient={data.arpu.insufficient_data} hint={`${data.arpu.paying_users} paying`} testid="an-arpu" />
          <KPI title={t("adminp.analytics.churn")} value={fmtPct(data.churn.churn_rate)} insufficient={data.churn.insufficient_data} hint={`${data.churn.cancellations} canceled / ${data.churn.active_at_start} active`} testid="an-churn" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI title={t("adminp.analytics.credits_sold")} value={fmtNum(data.credits.credits_sold)} insufficient={data.credits.insufficient_data} testid="an-credits-sold" />
          <KPI title={t("adminp.analytics.credits_spent")} value={fmtNum(data.credits.credits_spent)} insufficient={data.credits.insufficient_data} testid="an-credits-spent" />
          <KPI title={t("adminp.analytics.liability")} value={fmtNum(data.credits.credits_outstanding_liability)} tone="warning" testid="an-credits-liab" />
          <KPI title={t("adminp.analytics.api_success")} value={fmtPct(data.api_usage.success_rate)} insufficient={data.api_usage.insufficient_data} hint={`${fmtNum(data.api_usage.total_requests)} req`} testid="an-api" />
        </div>

        <SectionCard title={`${t("adminp.analytics.trend")} · ${days}d`}
          description={`First: ${data.daily_series[0]?.date || "—"} · Last: ${data.daily_series[data.daily_series.length - 1]?.date || "—"} · Max: ${fmtCents(Math.max(...data.daily_series.map((d) => d.gross_cents), 1))}`}>
          <svg viewBox={`0 0 ${Math.max(data.daily_series.length * 20, 100)} 100`} className="w-full h-32" preserveAspectRatio="none">
            {data.daily_series.map((d, i) => {
              const maxG = Math.max(...data.daily_series.map((x) => x.gross_cents), 1);
              const h = (d.gross_cents / maxG) * 90;
              return <rect key={d.date} x={i * 20 + 2} y={100 - h} width={16} height={h} fill="rgba(16, 185, 129, 0.6)" data-testid={`an-bar-${i}`} />;
            })}
          </svg>
        </SectionCard>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TopList title={t("adminp.analytics.top_products")} items={data.top_products.items} insufficient={data.top_products.insufficient_data} render={(p) => [p.product_name, fmtNum(p.requests), p.product_id]} />
          <TopList title={t("adminp.analytics.top_customers")} items={data.top_customers.items} insufficient={data.top_customers.insufficient_data} render={(c) => [c.user_email || c.user_id?.slice(0, 8), fmtCents(c.net_cents), c.user_id]} />
          <TopList title={t("adminp.analytics.top_plans")} items={data.top_plans.items} insufficient={data.top_plans.insufficient_data} render={(p) => [p.plan_display_name || p.plan_tier, fmtNum(p.active_subscriptions), p.plan_id]} />
        </div>
      </>)}
    </div>
  );
}

function TopList({ title, items, insufficient, render }) {
  return (
    <SectionCard title={title}>
      {insufficient || !items?.length ? (
        <EmptyState title="Insufficient data" />
      ) : (
        <ul className="text-xs space-y-1">
          {items.map((it) => {
            const [label, value, key] = render(it);
            return (
              <li key={key} className="flex justify-between border-t border-white/5 pt-1 gap-2">
                <span className="text-white/85 truncate">{label}</span>
                <span className="text-white/60 tabular-nums shrink-0">{value}</span>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}
